import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight, TwoCol } from "@/components/Insight";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp, Anatomy,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation7() {
  const { lesson, course, accent } = findLesson("piano-foundation", "7");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF7-Posture-Animated.mp4"
        caption={`5 anatomical anchors — back · core · shoulders · wrist · fingers — each lit in sequence`}
        hint={`≈ 22 sec · the whole chain plays the piano`}
      />
      <KeyHighlights>
        <Highlight>Find your bench height by <strong>standing and miming eating at a fast-food counter</strong> — elbows naturally raised, shoulders relaxed. That's the playing height.</Highlight>
        <Highlight>Use an <strong>adjustable chair</strong>. If it has armrests, remove them — armrests kill your crossing, gliding, and arpeggios.</Highlight>
        <Highlight>Engage the <strong>strong muscles first</strong> — back, core, shoulders. Then the small muscles (wrist, fingers) only do the fine work.</Highlight>
        <Highlight>Wrist <strong>does not drop down</strong>. Fingers stay slightly bent — imagine holding an orange or a tennis ball. The third joint never collapses on a strike.</Highlight>
        <Highlight>Press white keys <strong>just under the black keys</strong>, not at the edge — so jumping to a black key is a tiny move, not a long reach.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The chair is the foundation" title="The fast-food-counter test" accent={accent.hex}>
        <p>
          Before any drill, set up the bench. The simplest test: stand up as if you're eating
          at an Indian fast-food counter.
        </p>

        <Pull>
          When you stand you keep your elbows relaxed and you just raise your elbows.
          You don't need to raise your shoulders at all.
        </Pull>

        <p>
          Stand up first. Mime that eating position. Now squat down — and where your hands
          naturally fall is exactly where the keyboard should sit. From a sitting position,
          your forearms should be roughly parallel to the floor, your elbows neither hunched
          high nor stretched low.
        </p>

        <Anatomy title="The bench-height check">
          <svg viewBox="0 0 400 230" className="w-full max-w-2xl">
            {/* keyboard surface */}
            <rect x="40" y="135" width="320" height="14" fill="#1f2937" stroke="#9aa1ad" strokeWidth="1"/>
            <text x="200" y="165" textAnchor="middle" fill="#9aa1ad" fontSize="11">keyboard surface</text>
            {/* body */}
            <ellipse cx="200" cy="55" rx="22" ry="26" fill="none" stroke="#f59e0b" strokeWidth="2"/>
            <line x1="200" y1="80" x2="200" y2="190" stroke="#f59e0b" strokeWidth="2"/>
            <text x="220" y="130" fill="#f59e0b" fontSize="10">spine straight, core engaged</text>
            {/* arms — elbows at key height */}
            <line x1="200" y1="100" x2="160" y2="135" stroke="#22c55e" strokeWidth="2"/>
            <line x1="200" y1="100" x2="240" y2="135" stroke="#22c55e" strokeWidth="2"/>
            <line x1="160" y1="135" x2="155" y2="143" stroke="#22c55e" strokeWidth="2"/>
            <line x1="240" y1="135" x2="245" y2="143" stroke="#22c55e" strokeWidth="2"/>
            <text x="100" y="120" fill="#22c55e" fontSize="10">elbows ≈ key height</text>
            <text x="265" y="120" fill="#22c55e" fontSize="10">forearm parallel</text>
            {/* bench */}
            <rect x="120" y="190" width="160" height="10" fill="#3b82f6" opacity="0.6"/>
            <text x="200" y="215" textAnchor="middle" fill="#3b82f6" fontSize="11">bench (adjustable · no armrests)</text>
          </svg>
        </Anatomy>

        <Tip>
          Computer chair is fine — back support helps for long sessions. But if it has
          armrests, take them off or pick a different chair. Armrests block crossing and
          gliding hand movements; the lessons coming up depend on those.
        </Tip>
      </Insight>

      <Insight eyebrow="Strong muscles first" title="Engage the back and core, then forget about them" accent={accent.hex}>
        <p>
          Most beginners think piano is a finger sport. It isn't — it's a whole-body sport,
          and the heavy lifting is done by muscles you can't see. The order:
        </p>

        <Steps title="Activate before you play">
          <Step>
            <strong>Core.</strong> Engage as if someone's about to throw a punch — that's the
            level of activation. It strengthens the back automatically.
          </Step>
          <Step>
            <strong>Back.</strong> Sit straight. Don't slump. The back and core working
            together hold the rest of the body upright effortlessly.
          </Step>
          <Step>
            <strong>Shoulders down.</strong> Don't droop, don't hunch. The moment they fall,
            the back gives up and everything funnels into the small muscles you don't want
            doing the work.
          </Step>
        </Steps>

        <KeyIdea>
          Strong muscles handle posture; small muscles handle precision. Posture done right
          means the wrist and fingers only do the fine work — the heavy lifting happens at
          the back.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Wrist and fingers" title="Wrist up · third joint never collapses" accent={accent.hex}>
        <Pull>
          99% of all the pain you get on the piano, I can guarantee you, is pain that
          you are causing yourself. It's not the piano. It's just you with incorrect
          posture.
        </Pull>

        <TwoCol>
          <div>
            <div className="text-xs uppercase tracking-wider text-emerald-300">Wrist</div>
            <ul className="mt-2 space-y-1 text-[13px] text-nsm-ink">
              <li>Don't drop downward — that hurts the forearm.</li>
              <li>Keep it floating slightly above the keys.</li>
              <li>If it gets tense, let it drop briefly, then push back up with the forearm — natural up-and-down release.</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-rose-300">Fingers</div>
            <ul className="mt-2 space-y-1 text-[13px] text-nsm-ink">
              <li>Bend the joint slightly. Imagine holding an orange or a tennis ball.</li>
              <li>Strike like a hammer — from the knuckle, with the fingertip.</li>
              <li>The <em>top joint</em> (closest to the fingertip) must never collapse on a strike.</li>
              <li>Don't play flat-fingered. Don't slide fingers across keys.</li>
            </ul>
          </div>
        </TwoCol>
      </Insight>

      <Insight eyebrow="Where to press" title="Just under the black keys, not at the edge" accent={accent.hex}>
        <p>
          A small geometry trick: when you press a white key, press it{" "}
          <strong>further into the keyboard</strong> — just below where the black keys live —
          rather than at the very edge nearest you. The reason is functional: when a black key
          comes up next, it's a tiny move from there, not a long reach forward.
        </p>

        <p>
          The thumb is special — because it's short, it has to attack <em>vertically</em>. Let
          it strike straight down. The other fingers can stay slightly curled.
        </p>
      </Insight>

      <Insight eyebrow="The honest truth" title="Posture isn't make-or-break — but it pays off" accent={accent.hex}>
        <p>
          Posture isn't going to make or break your piano playing, and you don't have to get
          all of it right at once: <em>"Even if you don't follow 100% of what I just said in
          this video and just followed a little bit, you know, 20–30%, just get your height
          fine and look at it as a work in progress, things are going to get really, really
          well oiled in the near future."</em>
        </p>

        <p>
          The point of paying attention is that you want to play the piano longer and longer
          over the years. Bad posture starts hurting around year three of serious practice.
          Catching it early means you're not retraining muscle memory in your forties.
        </p>

        <Tip>
          Watch your computer use too. Most repetitive-stress and carpal-tunnel issues
          pianists develop didn't come from the piano — they came from a desk job. The wrist
          rules from this lesson apply to a keyboard-and-mouse setup just as much.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-8"
        label="Lesson 8 · Playing Melodies on Multiple Scales (using Intervals)"
        why="With the body sorted, the next jump is intellectual: how to play any melody you know in any key — by thinking in scale-degree numbers instead of letter names."
      />
    </LessonShell>
  );
}
